import { Progress } from '../components/ui/progress';
import { CheckCircle2, AlertCircle, MinusCircle } from 'lucide-react';

function getScoreColor(score) {
  if (score >= 70) return { text: 'text-green-600', bg: 'bg-green-500', icon: CheckCircle2 };
  if (score >= 40) return { text: 'text-yellow-600', bg: 'bg-yellow-500', icon: MinusCircle };
  return { text: 'text-red-600', bg: 'bg-red-500', icon: AlertCircle };
}

export function QualificationMetrics({ scores, categories, overallScore }) {
  const safeScores = scores || {};
  const safeCategories = categories || [];
  const safeOverall = overallScore ?? 0;

  // Case-insensitive score lookup — AI may return "budget" or "Budget"
  const getScore = (category) => {
    if (safeScores[category] !== undefined) return safeScores[category];
    const lowerCategory = category.toLowerCase();
    for (const [key, value] of Object.entries(safeScores)) {
      if (key.toLowerCase() === lowerCategory) return value;
    }
    return 0;
  };

  return (
    <div className="space-y-6" data-testid="qualification-metrics">
      {/* Overall Score */}
      <div className="bg-white border border-zinc-200 rounded-md p-4" data-testid="overall-score-card">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
            Overall Score
          </span>
          {(() => {
            const { icon: Icon, text } = getScoreColor(safeOverall);
            return <Icon className={`w-5 h-5 ${text}`} />;
          })()}
        </div>
        <div className="flex items-end gap-2">
          <span 
            className={`text-4xl font-bold ${getScoreColor(safeOverall).text}`} 
            style={{ fontFamily: 'var(--font-mono)' }}
            data-testid="overall-score-value"
          >
            {safeOverall}
          </span>
          <span className="text-zinc-400 text-lg mb-1">/100</span>
        </div>
        <Progress 
          value={safeOverall} 
          className="h-2 mt-3 rounded-none" 
          data-testid="overall-score-progress"
        />
      </div>

      {/* Category Scores */}
      <div className="space-y-3">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
          Category Breakdown
        </span>
        
        {safeCategories.map((category) => {
          const score = getScore(category);
          const { text, bg, icon: Icon } = getScoreColor(score);
          
          return (
            <div 
              key={category} 
              className="bg-white border border-zinc-200 rounded-md p-3 card-hover"
              data-testid={`category-score-${category.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-zinc-900">{category}</span>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-mono font-bold ${text}`}>{score}%</span>
                  <Icon className={`w-4 h-4 ${text}`} />
                </div>
              </div>
              <div className="w-full bg-zinc-100 rounded-none h-1.5 overflow-hidden">
                <div 
                  className={`h-full ${bg} progress-animate rounded-none`}
                  style={{ width: `${score}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="pt-2 border-t border-zinc-200">
        <div className="flex items-center justify-between text-xs text-zinc-500">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-red-500" />
            <span>Needs work (0-39)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-yellow-500" />
            <span>Partial (40-69)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <span>Strong (70+)</span>
          </div>
        </div>
      </div>
    </div>
  );
}
